import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { memberApi } from '../api';
import { memberPath } from '@/config/hosts';
import { IconDoc } from '../components/Icons';

const EXPLAIN_PROMPT =
  'I attached my report. Please read it and explain the names and numbers in plain language. What should I confirm with my oncology team, and what is worth asking at the next visit? Do not diagnose or change treatment.';

function statusInfo(doc) {
  const extraction = String(doc?.extraction_status || '').toLowerCase();
  const status = String(doc?.status || '').toLowerCase();
  const scan = String(doc?.scan_status || '').toLowerCase();
  if (status === 'quarantined' || scan === 'blocked' || scan === 'quarantined') {
    return { label: 'Blocked', className: 'is-blocked' };
  }
  if (extraction === 'complete' || status === 'ready') {
    return { label: 'Ready', className: 'is-ready' };
  }
  if (extraction === 'failed') {
    return { label: 'Could not read', className: 'is-blocked' };
  }
  return { label: 'Reading…', className: 'is-reading' };
}

function formatBytes(n) {
  const size = Number(n) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export default function RecordDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pollRef = useRef(null);
  const analyzedRef = useRef(false);
  const analyzingRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const data = await memberApi.document(id);
      setDoc(data);
      setError('');
      return data;
    } catch (err) {
      setError(err?.message || 'Could not load this report.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    analyzedRef.current = false;
    analyzingRef.current = false;
    setAnalysis(null);
    setLoading(true);
    load();
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [load]);

  useEffect(() => {
    const needsPoll = doc && statusInfo(doc).className === 'is-reading';
    if (!needsPoll) {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = window.setInterval(() => {
      load();
    }, 3500);
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [doc, load]);

  const runAnalyze = useCallback(async (row) => {
    if (!row || statusInfo(row).className !== 'is-ready' || analyzingRef.current) return;
    analyzingRef.current = true;
    setAnalyzing(true);
    setError('');
    try {
      const result = await memberApi.analyzeDocument(row.id);
      setAnalysis(result);
      if (result.understanding) {
        setDoc((prev) =>
          prev
            ? {
                ...prev,
                understanding: result.understanding,
                has_understanding: true,
                report_type: result.report_type || prev.report_type,
                report_type_label: result.report_type_label || prev.report_type_label,
                facts: result.facts || prev.facts,
              }
            : prev
        );
      }
    } catch (err) {
      if (err?.status === 402) {
        setError(err.message || 'Not enough credits to deepen this explanation.');
      } else {
        setError(err?.message || 'Could not explain this report yet.');
      }
    } finally {
      analyzingRef.current = false;
      setAnalyzing(false);
    }
  }, []);

  useEffect(() => {
    if (!doc || analyzedRef.current) return;
    if (statusInfo(doc).className !== 'is-ready') return;
    analyzedRef.current = true;
    const hasLlm =
      doc.understanding?.source === 'llm' &&
      (doc.understanding?.summary || doc.understanding?.headline);
    if (hasLlm) {
      setAnalysis({
        understanding: doc.understanding,
        facts: doc.facts || [],
        report_type: doc.report_type,
        report_type_label: doc.report_type_label,
        disclaimer: doc.understanding?.disclaimer,
      });
      return;
    }
    runAnalyze(doc);
  }, [doc, runAnalyze]);

  const understanding = useMemo(() => {
    return analysis?.understanding || doc?.understanding || null;
  }, [analysis, doc]);

  const facts = analysis?.facts || doc?.facts || [];
  const status = statusInfo(doc || {});
  const ready = status.className === 'is-ready';
  const failed = status.className === 'is-blocked' && String(doc?.extraction_status || '').toLowerCase() === 'failed';

  const retry = async () => {
    if (retrying || !doc) return;
    setRetrying(true);
    setError('');
    analyzedRef.current = false;
    try {
      const next = await memberApi.retryDocument(doc.id);
      setDoc(next);
    } catch (err) {
      setError(err?.message || 'Could not retry reading this file.');
    } finally {
      setRetrying(false);
    }
  };

  const openChat = () => {
    navigate(`${memberPath('/')}?doc=${encodeURIComponent(id)}`);
  };

  if (loading) {
    return (
      <div className="by-page">
        <div className="by-center">
          <p className="by-fine">Opening your report…</p>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="by-page">
        <div className="by-empty">
          <div className="glyph"><IconDoc /></div>
          <h2>Report not found</h2>
          <p>{error || 'This record may have been deleted.'}</p>
          <Link className="by-btn by-btn-dark" to={memberPath('/records')}>Back to Records</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="by-page">
      <div className="by-center by-record-detail">
        <div className="by-record-nav">
          <Link className="by-fine" to={memberPath('/records')}>← All records</Link>
        </div>

        <header className="by-record-hero">
          <div>
            <h1>{doc.filename || 'Report'}</h1>
            <div className="by-records-meta">
              <span className={`by-status-pill ${status.className}`}>{status.label}</span>
              {doc.report_type_label ? <span>{doc.report_type_label}</span> : null}
              {doc.kind ? <span>{String(doc.kind).toUpperCase()}</span> : null}
              {doc.size ? <span>{formatBytes(doc.size)}</span> : null}
              {doc.created_at ? (
                <span>
                  {new Date(doc.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              ) : null}
            </div>
          </div>
          <div className="by-row">
            {ready ? (
              <button type="button" className="by-btn by-btn-dark" onClick={openChat}>
                Ask in chat
              </button>
            ) : null}
            {doc.download_url ? (
              <a className="by-btn by-btn-ghost" href={doc.download_url} target="_blank" rel="noreferrer">
                Download original
              </a>
            ) : null}
            {failed ? (
              <button type="button" className="by-btn by-btn-ghost" disabled={retrying} onClick={retry}>
                {retrying ? 'Retrying…' : 'Try reading again'}
              </button>
            ) : null}
          </div>
        </header>

        {error ? <p className="by-fine by-record-error">{error}</p> : null}

        {!ready && !failed ? (
          <div className="by-empty by-record-panel">
            <div className="glyph"><IconDoc /></div>
            <h2>Reading your report</h2>
            <p>We are securely extracting the text. This usually takes a few seconds.</p>
          </div>
        ) : null}

        {failed ? (
          <div className="by-empty by-record-panel">
            <div className="glyph"><IconDoc /></div>
            <h2>Could not read this file</h2>
            <p>
              Try again, or upload a clearer PDF or photo. Scanned pages and DOCX files are supported when the scan is clean.
            </p>
          </div>
        ) : null}

        {ready ? (
          <>
            <section className="by-record-panel">
              <div className="by-record-panel-head">
                <h2>Plain-language reading</h2>
                {analyzing ? <span className="by-fine">Deepening explanation…</span> : null}
                {!analyzing && understanding?.source === 'heuristic' ? (
                  <button type="button" className="by-btn by-btn-ghost" onClick={() => runAnalyze(doc)}>
                    Improve explanation
                  </button>
                ) : null}
              </div>
              {understanding?.headline ? <p className="by-record-headline">{understanding.headline}</p> : null}
              {understanding?.summary
                ? String(understanding.summary)
                    .split(/\n+/)
                    .filter(Boolean)
                    .map((para, idx) => (
                      <p className="by-record-summary" key={`sum-${idx}`}>{para}</p>
                    ))
                : (
                  <p className="by-fine">
                    {analyzing
                      ? 'Writing a clearer explanation…'
                      : 'Open Ask in chat for a conversational walkthrough.'}
                  </p>
                )}
              {(understanding?.key_points || []).length ? (
                <ul className="by-record-points">
                  {understanding.key_points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              ) : null}
              <p className="by-fine by-record-disclaimer">
                {understanding?.disclaimer ||
                  analysis?.disclaimer ||
                  'This is a plain-language reading aid you can bring to clinic.'}
              </p>
            </section>

            {(understanding?.numbers || []).length || facts.length ? (
              <section className="by-record-panel">
                <h2>Numbers from the report</h2>
                <p className="by-fine">Copied from the document text. Not clinically verified.</p>
                <div className="by-record-table-wrap">
                  <table className="by-record-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Value</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(understanding?.numbers || []).length
                        ? understanding.numbers.map((row) => (
                            <tr key={`${row.label}-${row.value}`}>
                              <td>{row.label}</td>
                              <td>{row.value}</td>
                              <td>{row.note || '-'}</td>
                            </tr>
                          ))
                        : facts.map((row) => (
                            <tr key={`${row.test}-${row.value}`}>
                              <td>{row.test}</td>
                              <td>{[row.value, row.unit].filter(Boolean).join(' ')}</td>
                              <td>-</td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            {(understanding?.terms || []).length ? (
              <section className="by-record-panel">
                <h2>Words explained</h2>
                <dl className="by-record-glossary">
                  {understanding.terms.map((item) => (
                    <div key={item.term}>
                      <dt>{item.term}</dt>
                      <dd>{item.plain}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}

            {(understanding?.questions_for_team || []).length ? (
              <section className="by-record-panel">
                <h2>Ask your care team</h2>
                <ul className="by-record-points">
                  {understanding.questions_for_team.map((q) => (
                    <li key={q}>{q}</li>
                  ))}
                </ul>
                <div className="by-row" style={{ marginTop: 12 }}>
                  <button type="button" className="by-btn by-btn-dark" onClick={openChat}>
                    Discuss these in chat
                  </button>
                </div>
              </section>
            ) : null}

            <p className="by-fine" style={{ marginTop: 8 }}>
              Prefer a conversation? Use “Ask in chat” with: {EXPLAIN_PROMPT.slice(0, 72)}…
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
